import { useEffect } from 'react';
import { useQuery } from 'urql';

import { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { getInstagramCreator } from '../graphql';

interface UseInstagramCreatorOverviewProps {
  instagramAccount?: CreatorDiscoveryInstagramAccount;
  creatorUsername: string | undefined | null;
  clientId: string | undefined | null;
  onSheetShouldClose: () => void;
  processCreatorClick: (creator: Pick<InstagramCreator, 'username'>) => void;
  clickedCreators: InstagramCreator[];
  originalCreator: Pick<InstagramCreator, 'username'> | null;
  setOriginalCreator: (creator: Pick<InstagramCreator, 'username'> | null) => void;
  similarCreatorsData?: InstagramCreator[] | null;
  isLoadingSimilarCreators?: boolean;
}

export const useInstagramCreatorOverview = ({
  instagramAccount,
  creatorUsername,
  clientId,
  onSheetShouldClose,
  processCreatorClick,
  clickedCreators,
  originalCreator,
  setOriginalCreator,
  similarCreatorsData,
  isLoadingSimilarCreators = false,
}: UseInstagramCreatorOverviewProps) => {
  const { igUserId } = instagramAccount ?? {};

  const [{ data, fetching }] = useQuery({
    query: getInstagramCreator,
    variables: {
      username: creatorUsername,
      igUserId,
      clientId,
    },
    pause: !creatorUsername || !igUserId || !clientId,
  });

  const fetchedCreatorData = data?.getInstagramCreator?.creator as InstagramCreator | null | undefined;
  const isOpen = !!creatorUsername && !!igUserId;

  // Manage originalCreator lifecycle
  useEffect(() => {
    // Set originalCreator when overview opens for the first time
    if (isOpen && !originalCreator && creatorUsername && igUserId) {
      setOriginalCreator({ username: creatorUsername });
    }

    // Clear originalCreator when overview closes
    if (!isOpen && originalCreator) {
      setOriginalCreator(null);
    }
  }, [isOpen, originalCreator, creatorUsername, igUserId, setOriginalCreator]);

  // Calculate showReturnButton
  const showReturnButton = Boolean(
    originalCreator &&
    creatorUsername &&
    originalCreator.username !== creatorUsername
  );

  const handleReturnToOriginalCreator = () => {
    if (originalCreator) {
      processCreatorClick({
        username: originalCreator.username,
      });
    }
  };

  return {
    instagramCreatorOverviewProps: {
      open: isOpen,
      onOpenChange: (sheetOpenState: boolean) => {
        if (!sheetOpenState) {
          onSheetShouldClose();
        }
      },
      creator: fetchedCreatorData || null,
      isLoading: fetching || isLoadingSimilarCreators,
      discoveredCreators: similarCreatorsData || [],
      onCreatorChange: processCreatorClick,
      clickedCreators,
      originalCreator,
      onReturnToOriginalCreator: handleReturnToOriginalCreator,
      showReturnButton,
    },
  };
};

export default useInstagramCreatorOverview;
